import {
  GENERATION_IMPACT_HASH_VERSION,
  type GenerationImpactScenarioInput,
  type GenerationImpactScenarioSnapshot,
} from '../../types/generationImpact';
import { validateGenerationEquipmentBasis } from './generationEquipmentCatalog';
import { validateGenerationBomBasis } from './generationBomBasis';
import { validateGenerationProjectDutyBasis } from './generationProjectDuty';
import {
  hashGenerationImpactPayload,
  hashGenerationImpactValue,
} from './integrity';

export { hashGenerationImpactPayload };

export interface GenerationImpactScenarioIntegrityResult {
  valid: boolean;
  errors: string[];
}

/** Verify that the frozen scenario input and result still match their persisted digests. */
export function validateGenerationImpactScenarioIntegrity(
  candidate: unknown,
): GenerationImpactScenarioIntegrityResult {
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
    return { valid: false, errors: ['Generation impact scenario snapshot is not an object.'] };
  }
  const source = candidate as Record<string, unknown>;
  const errors: string[] = [];
  if (source.hashVersion != null && source.hashVersion !== GENERATION_IMPACT_HASH_VERSION) {
    errors.push(`Unsupported generation impact hash version: ${String(source.hashVersion)}.`);
  }
  if (!source.input || typeof source.input !== 'object' || Array.isArray(source.input)) {
    errors.push('Generation impact scenario input is missing or malformed.');
  } else {
    const input = source.input as Record<string, unknown>;
    if (typeof source.siteId !== 'string'
      || typeof source.name !== 'string'
      || source.siteId !== input.siteId
      || source.name !== input.name
      || (source.targetPhaseId ?? undefined) !== (input.targetPhaseId ?? undefined)) {
      errors.push('Generation impact scenario snapshot identity differs from its hashed input.');
    }
    if (Object.prototype.hasOwnProperty.call(input, 'equipmentBasis')) {
      errors.push(...validateGenerationEquipmentBasis(input.equipmentBasis)
        .map(error => `Generation impact equipment basis ${error}.`));
    }
    if (Object.prototype.hasOwnProperty.call(input, 'projectDutyBasis')) {
      errors.push(...validateGenerationProjectDutyBasis(
        input.projectDutyBasis,
        input as unknown as GenerationImpactScenarioInput,
      )
        .map(error => `Generation impact project duty basis ${error}.`));
    }
    if (Object.prototype.hasOwnProperty.call(input, 'bomBasis')) {
      errors.push(...validateGenerationBomBasis(input.bomBasis)
        .map(error => `Generation impact BOM basis ${error}.`));
    }
  }
  if (!source.result || typeof source.result !== 'object' || Array.isArray(source.result)) {
    errors.push('Generation impact scenario result is missing or malformed.');
  }
  for (const field of ['inputHash', 'resultHash'] as const) {
    if (typeof source[field] !== 'string' || !/^[a-f\d]{64}$/i.test(source[field] as string)) {
      errors.push(`Generation impact scenario ${field} is not a SHA-256 digest.`);
    }
  }
  if (errors.length > 0) return { valid: false, errors };

  try {
    // Explicit v1 snapshots are locked to JSON.stringify ordering. Unmarked
    // snapshots include both new canonical saves and historic pre-marker
    // records, so accept either convention only when one convention validates
    // the complete input/result pair.
    const hashers = source.hashVersion === GENERATION_IMPACT_HASH_VERSION
      ? [hashGenerationImpactPayload]
      : [hashGenerationImpactValue, hashGenerationImpactPayload];
    const comparisons = hashers.map(hashPayload => ({
      input: hashPayload(source.input) === source.inputHash,
      result: hashPayload(source.result) === source.resultHash,
    }));
    if (comparisons.some(match => match.input && match.result)) {
      return { valid: true, errors };
    }
    const inputMatches = comparisons.some(match => match.input);
    const resultMatches = comparisons.some(match => match.result);
    if (inputMatches && resultMatches) {
      errors.push('Generation impact scenario input and result use different integrity hash conventions.');
    } else {
      if (!inputMatches) {
        errors.push('Generation impact scenario inputHash does not match the frozen input.');
      }
      if (!resultMatches) {
        errors.push('Generation impact scenario resultHash does not match the frozen result.');
      }
    }
  } catch (error) {
    errors.push(`Generation impact scenario cannot be hashed: ${(error as Error).message}`);
  }
  return { valid: errors.length === 0, errors };
}

export function requireValidGenerationImpactScenarioIntegrity(
  candidate: unknown,
): GenerationImpactScenarioSnapshot {
  const validation = validateGenerationImpactScenarioIntegrity(candidate);
  if (!validation.valid) {
    throw new TypeError(`Invalid generation impact scenario: ${validation.errors.join(' ')}`);
  }
  return candidate as GenerationImpactScenarioSnapshot;
}
